import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Token store (sessionStorage = tab-scoped, cleared on tab close) ─────
// Used as a Bearer fallback when httpOnly cookies are stripped or
// scoped to a different domain than the frontend (common with Cloudflare
// in front of a separate API host).
const ACCESS_KEY = 'hre_access';
const REFRESH_KEY = 'hre_refresh';

export const tokenStore = {
  getAccess: () => {
    try { return sessionStorage.getItem(ACCESS_KEY); } catch { return null; }
  },
  getRefresh: () => {
    try { return sessionStorage.getItem(REFRESH_KEY); } catch { return null; }
  },
  set: (access, refresh) => {
    try {
      if (access) sessionStorage.setItem(ACCESS_KEY, access);
      if (refresh) sessionStorage.setItem(REFRESH_KEY, refresh);
    } catch (_) {}
  },
  clear: () => {
    try {
      sessionStorage.removeItem(ACCESS_KEY);
      sessionStorage.removeItem(REFRESH_KEY);
    } catch (_) {}
  },
};

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  // Still send cookies when possible (defense in depth). The Bearer header
  // is the authoritative auth source.
  withCredentials: true,
  timeout: 30000,
});

// Request interceptor — attach Bearer Authorization if we have one.
api.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Helper to extract API error message
export const getApiErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map(e => {
      if (typeof e === 'string') return e;
      if (e.msg) return e.msg;
      if (e.message) return e.message;
      return null;
    }).filter(Boolean);
    return messages.length > 0 ? messages.join(', ') : defaultMessage;
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || defaultMessage;
  }
  if (error?.message && typeof error.message === 'string') return error.message;
  return defaultMessage;
};

// ── 401 → silent refresh → retry once, then redirect ─────────────────
let isRefreshing = false;
let pendingQueue = [];

const flushQueue = (error, newToken) => {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      if (newToken && config) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
      }
      resolve(api(config));
    }
  });
  pendingQueue = [];
};

const PUBLIC_PATHS = [
  '/',
  '/demande-demo',
  '/landing-legacy',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  // Pages publiques sans authentification (trombi partagé, fiches publiques)
  '/shared/directory',
  '/public/employee',
  '/public/department',
];
const isPublicPath = () => PUBLIC_PATHS.some(p => window.location.pathname === p || window.location.pathname.startsWith(p + '/'));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = (original.url || '');
    const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/logout');

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          original._retry = true;
          pendingQueue.push({ resolve, reject, config: original });
        });
      }

      original._retry = true;
      isRefreshing = true;
      try {
        // Send refresh token in body too (Bearer fallback when cookies
        // are not stored by the browser for our domain).
        const refreshToken = tokenStore.getRefresh();
        const r = await api.post('/auth/refresh', refreshToken ? { refreshToken } : {});
        const newAccess = r.data?.accessToken;
        const newRefresh = r.data?.refreshToken;
        if (newAccess) tokenStore.set(newAccess, newRefresh);
        flushQueue(null, newAccess);
        if (newAccess) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newAccess}`;
        }
        return api(original);
      } catch (refreshErr) {
        flushQueue(refreshErr);
        tokenStore.clear();
        if (!isPublicPath()) {
          localStorage.removeItem('lastActivity');
          setTimeout(() => {
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }, 100);
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        error.message = 'La requête a pris trop de temps. Veuillez réessayer.';
      } else if (error.message === 'Network Error') {
        error.message = 'Erreur de connexion au serveur. Vérifiez votre connexion internet.';
      }
    }
    if (error.response?.status === 503) {
      error.response.data = { detail: 'Service temporairement indisponible. Veuillez réessayer dans quelques instants.' };
    }
    if (error.response?.status >= 500) {
      error.response.data = error.response.data || { detail: 'Une erreur serveur est survenue. Veuillez réessayer.' };
    }

    return Promise.reject(error);
  }
);

export default api;
